import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { supabase } from '../../lib/supabase';
import { useTheme, makeThemedStyles } from '../../v10/theme';
import { GlassFill } from '../../v10/ui';

// ---------------------------------------------------------------------------
// NEMOS Files — native replica of src/app/nemos/files/page.tsx, LOCAL DRIVE
// mode only. Same `hrmez_local_folders` / `hrmez_local_files` tables and the
// same `hrmez-files` storage bucket as web.
//
// Web-only remainder (reported honestly, not silently dropped):
//  - Google Drive mode (driveMode === 'google', /api/hrmez/drive proxy,
//    OAuth-connected browsing) is NOT ported. It depends on a server-side
//    Google OAuth token flow that has no mobile client equivalent here.
//    This view always operates in local-drive mode and shows a banner
//    if the user's hrmez_config.drive_mode is 'google', explaining that
//    Google Drive browsing is available on web only.
//  - Generic multi-file upload (web's <input type="file multiple">) IS
//    ported via expo-document-picker's getDocumentAsync({ multiple: true }),
//    matching web's `handleFileUpload` storage path convention
//    (`${userId}/${currentFolderId}/${Date.now()}_${fileName}` in the
//    `hrmez-files` bucket) and hrmez_local_files row shape 1:1. A separate
//    "+ Image" button remains for the camera-roll picker flow (image assets
//    only, useful when the system document picker doesn't surface Photos on
//    some Android configurations).
//  - Grid/icon view and right-click context menu are collapsed into a
//    single list view with inline actions (tap opens folder / long-press
//    for actions), matching mobile's tap/long-press convention rather than
//    hover/context-menu.
// ---------------------------------------------------------------------------

interface LocalFolder {
  id: string;
  user_id: string;
  parent_id: string | null;
  name: string;
  created_at: string;
}

interface LocalFile {
  id: string;
  user_id: string;
  folder_id: string;
  name: string;
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

const DEFAULT_LOCAL_ROOTS = ['Personal', 'Finance', 'Health'];
const PARTS_FOLDERS = ['Projects', 'Areas', 'Resources', 'Tasks', 'Systems'];

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileGlyph(mimeType: string) {
  if (mimeType.startsWith('image/')) return '🖼';
  if (mimeType.startsWith('video/')) return '🎞';
  if (mimeType.startsWith('audio/')) return '🎵';
  if (mimeType.includes('spreadsheet') || mimeType.includes('csv') || mimeType.includes('excel')) return '📊';
  if (mimeType.includes('javascript') || mimeType.includes('json') || mimeType.includes('html') || mimeType.includes('css') || mimeType.includes('xml')) return '🧩';
  if (mimeType.includes('text') || mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word')) return '📄';
  return '📎';
}

export function NemosFiles({ userId }: { userId: string }) {
  const { t, c, neu } = useTheme();
  const styles = useStyles();
  const [driveMode, setDriveMode] = useState<'local' | 'google'>('local');

  const [localFolders, setLocalFolders] = useState<LocalFolder[]>([]);
  const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: 'root', name: 'My Files' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameTarget, setRenameTarget] = useState<LocalFolder | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<LocalFolder | null>(null);
  const [deleteFileTarget, setDeleteFileTarget] = useState<LocalFile | null>(null);
  const [actionsTarget, setActionsTarget] = useState<{ kind: 'folder'; folder: LocalFolder } | { kind: 'file'; file: LocalFile } | null>(null);

  const loadDriveMode = useCallback(async () => {
    const { data } = await supabase.from('hrmez_config').select('drive_mode').eq('user_id', userId).maybeSingle();
    setDriveMode(((data as any)?.drive_mode as 'local' | 'google') || 'local');
  }, [userId]);

  const loadLocalFolders = useCallback(
    async (parentId: string | null) => {
      setIsLoading(true);
      let query = supabase.from('hrmez_local_folders').select('*').eq('user_id', userId).order('name');
      query = parentId ? query.eq('parent_id', parentId) : query.is('parent_id', null);
      const { data, error } = await query;
      if (error) {
        setIsLoading(false);
        return;
      }
      if (!parentId && (!data || data.length === 0)) {
        const rootInserts = DEFAULT_LOCAL_ROOTS.map((name) => ({ user_id: userId, parent_id: null, name }));
        const { data: roots } = await supabase.from('hrmez_local_folders').insert(rootInserts).select();
        if (roots) {
          const subInserts = roots.flatMap((root: any) => PARTS_FOLDERS.map((name) => ({ user_id: userId, parent_id: root.id, name })));
          await supabase.from('hrmez_local_folders').insert(subInserts);
          setLocalFolders(roots as LocalFolder[]);
        }
      } else {
        setLocalFolders((data || []) as LocalFolder[]);
      }
      setIsLoading(false);
    },
    [userId]
  );

  const loadLocalFiles = useCallback(
    async (folderId: string | null) => {
      if (!folderId) {
        setLocalFiles([]);
        return;
      }
      const { data } = await supabase.from('hrmez_local_files').select('*').eq('user_id', userId).eq('folder_id', folderId).order('name');
      setLocalFiles((data || []) as LocalFile[]);
    },
    [userId]
  );

  useEffect(() => {
    loadDriveMode();
    loadLocalFolders(null);
  }, [loadDriveMode, loadLocalFolders]);

  const navigateToLocalFolder = async (folder: LocalFolder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    await Promise.all([loadLocalFolders(folder.id), loadLocalFiles(folder.id)]);
  };

  const navigateToBreadcrumb = async (index: number) => {
    const crumb = breadcrumbs[index];
    if (!crumb) return; // stale/rapid tap with an out-of-range index — ignore rather than crash
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    const parentId = crumb.id === 'root' ? null : crumb.id;
    setCurrentFolderId(parentId);
    await Promise.all([loadLocalFolders(parentId), loadLocalFiles(parentId)]);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    await supabase.from('hrmez_local_folders').insert({ user_id: userId, parent_id: currentFolderId || null, name: newFolderName.trim() });
    setShowNewFolder(false);
    setNewFolderName('');
    await loadLocalFolders(currentFolderId);
  };

  const handleRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    await supabase.from('hrmez_local_folders').update({ name: renameValue.trim() }).eq('id', renameTarget.id);
    setRenameTarget(null);
    await loadLocalFolders(currentFolderId);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await supabase.from('hrmez_local_folders').delete().eq('id', deleteTarget.id);
    setDeleteTarget(null);
    await loadLocalFolders(currentFolderId);
  };

  const handleDeleteFile = async () => {
    if (!deleteFileTarget) return;
    await supabase.storage.from('hrmez-files').remove([deleteFileTarget.storage_path]);
    await supabase.from('hrmez_local_files').delete().eq('id', deleteFileTarget.id);
    setDeleteFileTarget(null);
    await loadLocalFiles(currentFolderId);
  };

  const handleUploadImage = async () => {
    if (!currentFolderId || isUploading) return;
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) {
      Alert.alert('Permission needed', 'Allow photo library access to upload a file.');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.9 });
    if (result.canceled || !result.assets?.[0]) return;
    const asset = result.assets[0];
    setIsUploading(true);
    try {
      const fileName = asset.fileName || `image_${Date.now()}.jpg`;
      const storagePath = `${userId}/${currentFolderId}/${Date.now()}_${fileName}`;
      const res = await fetch(asset.uri);
      const arrayBuffer = await res.arrayBuffer();
      const contentType = asset.mimeType || 'image/jpeg';
      const { error: uploadError } = await supabase.storage.from('hrmez-files').upload(storagePath, arrayBuffer, { contentType });
      if (uploadError) {
        Alert.alert('Upload failed', uploadError.message);
        return;
      }
      const { error: dbError } = await supabase.from('hrmez_local_files').insert({
        user_id: userId,
        folder_id: currentFolderId,
        name: fileName,
        storage_path: storagePath,
        mime_type: contentType,
        size_bytes: asset.fileSize || arrayBuffer.byteLength,
      });
      if (dbError) Alert.alert('Error', `Failed to save record for ${fileName}`);
      await loadLocalFiles(currentFolderId);
    } catch (err: any) {
      Alert.alert('Upload error', err?.message || 'Unknown error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadFiles = async () => {
    if (!currentFolderId || isUploading) return;
    const result = await DocumentPicker.getDocumentAsync({ type: '*/*', multiple: true, copyToCacheDirectory: true });
    if (result.canceled || !result.assets?.length) return;
    setIsUploading(true);
    try {
      for (const asset of result.assets) {
        const fileName = asset.name || `file_${Date.now()}`;
        const storagePath = `${userId}/${currentFolderId}/${Date.now()}_${fileName}`;
        const res = await fetch(asset.uri);
        const arrayBuffer = await res.arrayBuffer();
        const contentType = asset.mimeType || 'application/octet-stream';
        const { error: uploadError } = await supabase.storage.from('hrmez-files').upload(storagePath, arrayBuffer, { contentType });
        if (uploadError) {
          Alert.alert('Upload failed', `${fileName}: ${uploadError.message}`);
          continue;
        }
        const { error: dbError } = await supabase.from('hrmez_local_files').insert({
          user_id: userId,
          folder_id: currentFolderId,
          name: fileName,
          storage_path: storagePath,
          mime_type: contentType,
          size_bytes: asset.size || arrayBuffer.byteLength,
        });
        if (dbError) Alert.alert('Error', `Failed to save record for ${fileName}`);
      }
      await loadLocalFiles(currentFolderId);
    } catch (err: any) {
      Alert.alert('Upload error', err?.message || 'Unknown error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenFile = async (file: LocalFile) => {
    const { data, error } = await supabase.storage.from('hrmez-files').createSignedUrl(file.storage_path, 60);
    if (error || !data?.signedUrl) {
      Alert.alert('Error', error?.message || 'Could not open file.');
      return;
    }
    Alert.alert(file.name, `${formatFileSize(file.size_bytes)} · ${file.mime_type}\n\nSigned URL generated (open in browser to download).`);
  };

  return (
    <View style={styles.root}>
      {driveMode === 'google' && (
        <View style={styles.googleBanner}>
            <GlassFill />
          <Text style={styles.googleBannerText}>
            Your account is set to Google Drive mode. Google Drive browsing is available on web only — showing Local Drive here.
          </Text>
        </View>
      )}

      <View style={styles.toolbar}>
        {currentFolderId && (
          <>
            <Pressable style={styles.toolbarBtn} onPress={handleUploadFiles} disabled={isUploading}>
              <Text style={styles.toolbarBtnText}>{isUploading ? 'Uploading…' : '+ File'}</Text>
            </Pressable>
            <Pressable style={styles.toolbarBtn} onPress={handleUploadImage} disabled={isUploading}>
              <Text style={styles.toolbarBtnText}>+ Image</Text>
            </Pressable>
          </>
        )}
        <Pressable style={styles.toolbarBtn} onPress={() => setShowNewFolder(true)}>
          <Text style={styles.toolbarBtnText}>+ Folder</Text>
        </Pressable>
        <Pressable
          style={styles.toolbarBtn}
          onPress={() => {
            loadLocalFolders(currentFolderId);
            loadLocalFiles(currentFolderId);
          }}
        >
          <Text style={styles.toolbarBtnText}>↻</Text>
        </Pressable>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.breadcrumbScroll}>
        {breadcrumbs.map((crumb, i) => (
          <Pressable key={crumb.id} onPress={() => navigateToBreadcrumb(i)} style={styles.breadcrumbItem}>
            <Text style={[styles.breadcrumbText, i === breadcrumbs.length - 1 && styles.breadcrumbTextActive]}>
              {i === 0 ? 'Home' : crumb.name}
            </Text>
            {i < breadcrumbs.length - 1 && <Text style={styles.breadcrumbSep}>›</Text>}
          </Pressable>
        ))}
      </ScrollView>

      {isLoading ? (
        <ActivityIndicator color={t.goldBright} style={{ marginTop: 24 }} />
      ) : (
        <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
          {localFolders.map((folder) => (
            <Pressable
              key={folder.id}
              style={styles.row}
              onPress={() => navigateToLocalFolder(folder)}
              onLongPress={() => setActionsTarget({ kind: 'folder', folder })}
            >
              <Text style={styles.rowIcon}>📁</Text>
              <Text style={styles.rowName} numberOfLines={1}>{folder.name}</Text>
              <Text style={styles.rowMeta}>Folder</Text>
            </Pressable>
          ))}
          {localFiles.map((file) => (
            <Pressable
              key={file.id}
              style={styles.row}
              onPress={() => handleOpenFile(file)}
              onLongPress={() => setActionsTarget({ kind: 'file', file })}
            >
              <Text style={styles.rowIcon}>{fileGlyph(file.mime_type)}</Text>
              <Text style={styles.rowName} numberOfLines={1}>{file.name}</Text>
              <Text style={styles.rowMeta}>{formatFileSize(file.size_bytes)}</Text>
            </Pressable>
          ))}
          {localFolders.length === 0 && localFiles.length === 0 && (
            <Text style={styles.emptyText}>
              {currentFolderId ? 'Empty folder — upload images or create subfolders' : 'Empty — create folders to get started'}
            </Text>
          )}
        </ScrollView>
      )}

      {/* Actions sheet (long-press) — mobile substitute for web's right-click context menu */}
      <Modal visible={!!actionsTarget} transparent animationType="fade" onRequestClose={() => setActionsTarget(null)}>
        <Pressable style={styles.modalOverlay} onPress={() => setActionsTarget(null)}>
          <View style={styles.actionsSheet}>
            <GlassFill tone="strong" />
            {actionsTarget?.kind === 'folder' && (
              <>
                <Pressable
                  style={styles.actionRow}
                  onPress={() => {
                    setRenameTarget(actionsTarget.folder);
                    setRenameValue(actionsTarget.folder.name);
                    setActionsTarget(null);
                  }}
                >
                  <Text style={styles.actionText}>Rename</Text>
                </Pressable>
                <Pressable
                  style={styles.actionRow}
                  onPress={() => {
                    setDeleteTarget(actionsTarget.folder);
                    setActionsTarget(null);
                  }}
                >
                  <Text style={[styles.actionText, styles.actionTextDanger]}>Delete</Text>
                </Pressable>
              </>
            )}
            {actionsTarget?.kind === 'file' && (
              <>
                <Pressable
                  style={styles.actionRow}
                  onPress={() => {
                    handleOpenFile(actionsTarget.file);
                    setActionsTarget(null);
                  }}
                >
                  <Text style={styles.actionText}>Open</Text>
                </Pressable>
                <Pressable
                  style={styles.actionRow}
                  onPress={() => {
                    setDeleteFileTarget(actionsTarget.file);
                    setActionsTarget(null);
                  }}
                >
                  <Text style={[styles.actionText, styles.actionTextDanger]}>Delete</Text>
                </Pressable>
              </>
            )}
          </View>
        </Pressable>
      </Modal>

      <Modal visible={showNewFolder} transparent animationType="fade" onRequestClose={() => setShowNewFolder(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <GlassFill tone="strong" />
            <Text style={styles.modalTitle}>New Folder</Text>
            <TextInput style={styles.modalInput} value={newFolderName} onChangeText={setNewFolderName} placeholder="Folder name" placeholderTextColor={t.ivorySoft} onSubmitEditing={handleCreateFolder} />
            <View style={styles.modalButtons}>
              <Pressable style={styles.modalBtn} onPress={() => setShowNewFolder(false)}>
                <Text style={styles.modalBtnText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.modalBtn, styles.modalBtnPrimary]} onPress={handleCreateFolder} disabled={!newFolderName.trim()}>
                <Text style={[styles.modalBtnText, styles.modalBtnPrimaryText]}>Create</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!renameTarget} transparent animationType="fade" onRequestClose={() => setRenameTarget(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <GlassFill tone="strong" />
            <Text style={styles.modalTitle}>Rename</Text>
            <TextInput style={styles.modalInput} value={renameValue} onChangeText={setRenameValue} onSubmitEditing={handleRename} />
            <View style={styles.modalButtons}>
              <Pressable style={styles.modalBtn} onPress={() => setRenameTarget(null)}>
                <Text style={styles.modalBtnText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.modalBtn, styles.modalBtnPrimary]} onPress={handleRename} disabled={!renameValue.trim()}>
                <Text style={[styles.modalBtnText, styles.modalBtnPrimaryText]}>Rename</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!deleteTarget} transparent animationType="fade" onRequestClose={() => setDeleteTarget(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <GlassFill tone="strong" />
            <Text style={styles.modalTitle}>Delete &quot;{deleteTarget?.name}&quot;?</Text>
            <Text style={styles.modalBody}>This will permanently delete this folder and all its contents.</Text>
            <View style={styles.modalButtons}>
              <Pressable style={styles.modalBtn} onPress={() => setDeleteTarget(null)}>
                <Text style={styles.modalBtnText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.modalBtn, styles.modalBtnDanger]} onPress={handleDelete}>
                <Text style={[styles.modalBtnText, styles.modalBtnDangerText]}>Delete</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!deleteFileTarget} transparent animationType="fade" onRequestClose={() => setDeleteFileTarget(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <GlassFill tone="strong" />
            <Text style={styles.modalTitle}>Delete &quot;{deleteFileTarget?.name}&quot;?</Text>
            <Text style={styles.modalBody}>This will permanently delete this file.</Text>
            <View style={styles.modalButtons}>
              <Pressable style={styles.modalBtn} onPress={() => setDeleteFileTarget(null)}>
                <Text style={styles.modalBtnText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.modalBtn, styles.modalBtnDanger]} onPress={handleDeleteFile}>
                <Text style={[styles.modalBtnText, styles.modalBtnDangerText]}>Delete</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu, fonts, radii }) =>
  StyleSheet.create({
  root: { flex: 1, gap: 8 },
  googleBanner: { overflow: 'hidden', backgroundColor: 'transparent', borderRadius: radii.tile, padding: 10, borderWidth: 1, borderColor: t.navyLine },
  googleBannerText: { fontSize: 12, color: t.ivory },
  toolbar: { flexDirection: 'row', gap: 8 },
  toolbarBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: radii.pill, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  toolbarBtnText: { fontSize: 12, fontWeight: '800', color: t.goldBright },
  breadcrumbScroll: { flexGrow: 0 },
  breadcrumbItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  breadcrumbText: { fontSize: 12, color: t.ivorySoft, paddingVertical: 4 },
  breadcrumbTextActive: { color: t.ivory, fontWeight: '800' },
  breadcrumbSep: { fontSize: 12, color: t.ivorySoft, marginHorizontal: 4 },
  list: { flex: 1 },
  listContent: { paddingBottom: 24, gap: 2 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 8, paddingVertical: 10, borderRadius: radii.tile },
  rowIcon: { fontSize: 16 },
  rowName: { flex: 1, fontSize: 14, color: t.ivory },
  rowMeta: { fontSize: 11, color: t.ivorySoft },
  emptyText: { fontSize: 13, color: t.ivorySoft, textAlign: 'center', marginTop: 24 },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  actionsSheet: { backgroundColor: 'transparent', overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, borderRadius: radii.panel, minWidth: 220, boxShadow: `0 10px 30px ${t.glowSoft}` as any },
  actionRow: { paddingVertical: 14, paddingHorizontal: 18 },
  actionText: { fontSize: 15, fontWeight: '700', color: t.ivory },
  actionTextDanger: { color: c.danger },
  modalCard: { backgroundColor: 'transparent', overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, borderRadius: radii.panel, padding: 18, gap: 12, width: '100%', maxWidth: 380, boxShadow: `0 10px 30px ${t.glowSoft}` as any },
  modalTitle: { fontSize: 16, color: t.ivory, fontFamily: fonts.title, textTransform: 'uppercase', letterSpacing: 2.5 },
  modalBody: { fontSize: 13, color: t.ivorySoft, lineHeight: 18 },
  modalInput: { backgroundColor: c.inputFill, borderRadius: radii.tile, paddingHorizontal: 12, paddingVertical: 8, fontSize: 14, color: t.ivory, borderWidth: 1, borderColor: t.navyLine },
  modalButtons: { flexDirection: 'row', gap: 10, justifyContent: 'flex-end' },
  modalBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: radii.tile, backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  modalBtnText: { fontWeight: '800', color: t.ivory },
  modalBtnPrimary: { backgroundColor: t.gold },
  modalBtnPrimaryText: { color: t.onGold },
  modalBtnDanger: { backgroundColor: c.danger },
  modalBtnDangerText: { color: c.white },
}),
);
